/** @format */
import {
  useContext,
  useMemo,
  useState,
} from 'react';

import { Formik } from 'formik';
import {
  Link,
  useNavigate,
} from 'react-router-dom';
import * as Yup from 'yup';

import { EventContext } from '../../Contexts/EventContext';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import { error } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { usePasswordPolicy } from '../../Shared/hooks/usePasswordPolicy';
import useQuery from '../../Shared/queryString';
import { storeAuthToken } from '../../Shared/utils/authToken';
import { buildLoginPasswordFieldSchema } from '../../Shared/utils/passwordPolicy';
import {
  getApiErrorMessage,
  getApiErrorStatus,
} from '../../Shared/utils/helper';

const Login = () => {
  const passwordPolicy = usePasswordPolicy();
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        nat_code: Yup.string()
          .required('لطفا کد ملی را وارد نمایید')
          .length(10, 'کد ملی باید دقیقاً ۱۰ کاراکتر باشد'),
        password: buildLoginPasswordFieldSchema(passwordPolicy),
      }),
    [passwordPolicy],
  );

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const eventContext = useContext(EventContext);

  const onSubmitLoginForm = async (values, { setSubmitting }) => {
    setSubmitting(true);
    setLoading(true);

    await useApi.post(
      '/auth/login',
      values,
      (res) => {
        const accessToken = res.data?.data?.access_token.token;
        if (accessToken == null && res.data?.data?.register_token) {
          error(
            'مدت طولانی از تغیر رمز عبور شما گذشته است برای ادامه باید رمز عبور خود را تغیر دهید.',
          );
          setSubmitting(false);
          setLoading(false);
          navigate('/register/password', {
            state: {
              nat_code: values?.nat_code,
              register_token: res.data?.data?.register_token,
            },
          });
          return;
        }
        storeAuthToken(accessToken);

        const backUrl = useQuery.getParam('back');
        window.location.href = backUrl ? backUrl : '/panel/user/event';
      },
      (err) => {
        const status = getApiErrorStatus(err);
        if (status === 422) {
          error(
            getApiErrorMessage(
              err,
              'کد ملی و یا کلمه عبور اشتباه است',
            ),
          );
        } else if (status === 429) {
          error('تعداد درخواست‌ها زیاد است؛ لطفاً کمی بعد دوباره تلاش کنید.');
        } else if (status === 503 || !navigator.onLine) {
          error(
            navigator.onLine
              ? 'ارتباط با سرور برقرار نشد. لطفاً دوباره تلاش کنید.'
              : 'اتصال اینترنت برقرار نیست.',
          );
        } else {
          error('خطای ناخواسته‌ای رخ داده است');
        }
        setSubmitting(false);
        setLoading(false);
      },
    );

    setSubmitting(false);
    setLoading(false);
  };

  return (
    <div className='w-100 d-flex flex-column justify-content-center align-items-center bg-purple vh-100'>
      <section className='container'>
        <Formik
          initialValues={{ nat_code: '', password: '' }}
          validationSchema={validationSchema}
          onSubmit={onSubmitLoginForm}
        >
          {({ values, errors, handleChange, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className='row'>
                {/* لوگو و توضیحات */}
                <div className='col-12 d-flex flex-column p-2 text-center'>
                  <img
                    className='w-15 mx-auto'
                    src={eventContext?.setting?.logo?.logo ?? ''}
                    alt={eventContext?.setting?.name}
                  />
                  <h3 className='text-dark-mode f-14 my-3'>
                    برای ورود لطفا نام کاربری و رمز عبور خود را وارد کنید
                  </h3>
                  <div className='d-flex flex-row my-3 align-items-center'>
                    <hr className='w-30 bg-hr' />
                    <span className='text-dark-mode f-14 mx-2'>
                      ورود به سامانه {eventContext?.setting?.name ?? ''}
                    </span>
                    <hr className='w-30 bg-hr' />
                  </div>
                </div>

                {/* کد ملی */}
                <div className='col-12 col-md-6 offset-md-3'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      کد ملی
                    </label>
                    <input
                      type='text'
                      name='nat_code'
                      value={values.nat_code}
                      onChange={handleChange}
                      className='text-center dir-ltr form-control bg-dark-mode border-0 rounded-1 f-14 text-white'
                    />
                    <ErrorMessage error={errors.nat_code} classes='my-2 f-13' />
                  </div>
                </div>

                {/* کلمه عبور */}
                <div className='col-12 col-md-6 offset-md-3'>
                  <div className='form-group d-flex flex-column'>
                    <label className='text-dark-mode f-15 text-left my-2'>
                      کلمه عبور
                    </label>
                    <div className='position-relative'>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name='password'
                        value={values.password}
                        onChange={handleChange}
                        className='text-center dir-ltr form-control bg-dark-mode border-0 rounded-1 f-14 text-white pe-5'
                        autoComplete='current-password'
                      />
                      <button
                        type='button'
                        className='btn btn-link position-absolute top-50 end-0 translate-middle-y text-dark-mode p-0 px-2 border-0'
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={
                          showPassword
                            ? 'مخفی کردن کلمه عبور'
                            : 'نمایش کلمه عبور'
                        }
                        tabIndex={-1}
                      >
                        <i
                          className={`fad ${
                            showPassword ? 'fa-eye-slash' : 'fa-eye'
                          } f-16`}
                        />
                      </button>
                    </div>
                    <ErrorMessage error={errors.password} classes='my-2 f-13' />
                  </div>
                </div>

                {/* دکمه ورود */}
                <div className='col-12 col-md-6 offset-md-3'>
                  <div className='form-group d-flex flex-column'>
                    <button
                      type='submit'
                      className='btn btn-info rounded-1 my-2'
                      disabled={isSubmitting}
                    >
                      {loading ? (
                        <div
                          className='spinner-border spinner-border-sm text-white'
                          role='status'
                        />
                      ) : (
                        'ورود'
                      )}
                    </button>
                  </div>
                </div>
                <div className='col-12 col-md-6 offset-md-3'>
                  <div className='form-group d-flex flex-column'>
                    <Link
                      to='/register'
                      className='btn btn-secondary rounded-1 my-2'
                    >
                      ثبت نام
                    </Link>
                  </div>
                  <div className='col-12 col-md-6 offset-md-3'>
                    <div className='form-group d-flex flex-column'>
                      <Link
                        to='/forgot/password'
                        className='text-danger text-center my-2'
                      >
                        کلمه عبور خود را فراموش کرده ام
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </section>
    </div>
  );
};

export default Login;
